import { useState, useEffect } from "react";
import { useLoaderData, useFetcher, Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import type { BillingData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import "./BillingPage.css";

interface UrlResult {
  url: string;
}

const TIER_LABELS: Record<string, string> = {
  free: "Free",
  pro: "Pro",
  enterprise: "Enterprise",
};

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  active: { label: "Active", className: "status-active" },
  past_due: { label: "Past Due", className: "status-warning" },
  canceled: { label: "Canceled", className: "status-error" },
  incomplete: { label: "Incomplete", className: "status-warning" },
};

function formatDate(dateString: string | null): string {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

export default function BillingPage() {
  const { organization, billing, invoices } = useLoaderData() as BillingData;
  const [searchParams] = useSearchParams();
  const checkoutFetcher = useFetcher();
  const portalFetcher = useFetcher();
  const cancelFetcher = useFetcher();

  const [selectedInterval, setSelectedInterval] = useState<"yearly" | "monthly">("yearly");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  const isCheckoutLoading = checkoutFetcher.state === "submitting";
  const isPortalLoading = portalFetcher.state === "submitting";
  const isCancelLoading = cancelFetcher.state === "submitting";

  const statusInfo = STATUS_LABELS[billing.status] ?? {
    label: "Active",
    className: "status-active",
  };

  useEffect(() => {
    const checkout = searchParams.get("checkout");
    if (checkout === "success") {
      toast.success("Subscription activated successfully!");
    } else if (checkout === "canceled") {
      toast.error("Checkout was canceled");
    }
  }, [searchParams]);

  useEffect(() => {
    if (checkoutFetcher.data) {
      const result = checkoutFetcher.data as ActionResult<UrlResult>;
      if (result.success && result.data?.url) {
        window.location.href = result.data.url;
      } else if (result.error) {
        toast.error(result.error);
      }
    }
  }, [checkoutFetcher.data]);

  useEffect(() => {
    if (portalFetcher.data) {
      const result = portalFetcher.data as ActionResult<UrlResult>;
      if (result.success && result.data?.url) {
        window.location.href = result.data.url;
      } else if (result.error) {
        toast.error(result.error);
      }
    }
  }, [portalFetcher.data]);

  useEffect(() => {
    if (cancelFetcher.data) {
      const result = cancelFetcher.data as ActionResult;
      if (result.success) {
        toast.success("Subscription will be canceled at the end of the billing period");
        setShowCancelConfirm(false);
      } else if (result.error) {
        toast.error(result.error);
      }
    }
  }, [cancelFetcher.data]);

  if (!billing.stripeConfigured) {
    return (
      <>
        <Header />
        <div className="container">
          <div className="billing-container">
            <div className="billing-header">
              <h1>Billing</h1>
              <Link to={`/organizations/${organization.id}`} className="btn-secondary">
                Back to Organization
              </Link>
            </div>
            <div className="billing-section">
              <div className="billing-notice">
                <p>Billing is not configured for this application.</p>
                <p className="help-text">All features are unlocked.</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container">
        <div className="billing-container">
          <div className="billing-header">
            <h1>Billing</h1>
            <Link to={`/organizations/${organization.id}`} className="btn-secondary">
              Back to Organization
            </Link>
          </div>

          <div className="billing-section">
            <h2>Current Plan</h2>
            <div className="plan-status">
              <div className="plan-info">
                <div className="plan-tier">
                  <span className="tier-badge">{TIER_LABELS[billing.tier]}</span>
                  <span className={`status-badge ${statusInfo.className}`}>{statusInfo.label}</span>
                </div>
                {billing.tier !== "free" && billing.currentPeriodEnd && (
                  <p className="period-info">
                    {billing.cancelAtPeriodEnd ? (
                      <>Cancels on {formatDate(billing.currentPeriodEnd)}</>
                    ) : (
                      <>Renews on {formatDate(billing.currentPeriodEnd)}</>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="billing-section">
            <h2>Usage</h2>
            <div className="usage-grid">
              <div className="usage-card">
                <div className="usage-header">
                  <span className="usage-label">Members</span>
                  <span className="usage-count">
                    {billing.usage.seats} /{" "}
                    {billing.limits.maxSeats === null || billing.limits.maxSeats === Infinity
                      ? "∞"
                      : billing.limits.maxSeats}
                  </span>
                </div>
                <div className="usage-bar">
                  <div
                    className={`usage-fill ${billing.usage.seatsPercent >= 90 ? "usage-warning" : ""}`}
                    style={{ width: `${Math.min(billing.usage.seatsPercent, 100)}%` }}
                  />
                </div>
              </div>
              <div className="usage-card">
                <div className="usage-header">
                  <span className="usage-label">Teams</span>
                  <span className="usage-count">
                    {billing.usage.teams} /{" "}
                    {billing.limits.maxTeams === null || billing.limits.maxTeams === Infinity
                      ? "∞"
                      : billing.limits.maxTeams}
                  </span>
                </div>
                <div className="usage-bar">
                  <div
                    className={`usage-fill ${billing.usage.teamsPercent >= 90 ? "usage-warning" : ""}`}
                    style={{ width: `${Math.min(billing.usage.teamsPercent, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {billing.tier === "free" && (
            <div className="billing-section upgrade-section">
              <h2>Upgrade to Pro</h2>
              <p className="section-description">
                Unlock more seats, teams, and features with a Pro subscription.
              </p>

              <div className="interval-toggle">
                <button
                  type="button"
                  className={`interval-btn ${selectedInterval === "yearly" ? "active" : ""}`}
                  onClick={() => setSelectedInterval("yearly")}
                >
                  Yearly
                  <span className="discount-badge">Save 17%</span>
                </button>
                <button
                  type="button"
                  className={`interval-btn ${selectedInterval === "monthly" ? "active" : ""}`}
                  onClick={() => setSelectedInterval("monthly")}
                >
                  Monthly
                </button>
              </div>

              <div className="plan-card">
                <div className="plan-card-header">
                  <h3>Pro</h3>
                  <div className="price">
                    {selectedInterval === "yearly" ? (
                      <>
                        <span className="price-amount">$24</span>
                        <span className="price-period">/month</span>
                        <span className="price-note">billed annually</span>
                      </>
                    ) : (
                      <>
                        <span className="price-amount">$29</span>
                        <span className="price-period">/month</span>
                      </>
                    )}
                  </div>
                </div>
                <ul className="plan-features">
                  <li>Up to 10 members</li>
                  <li>Up to 5 teams</li>
                  <li>Priority support</li>
                </ul>
                <checkoutFetcher.Form
                  method="post"
                  action={`/organizations/${organization.id}/billing/checkout`}
                >
                  <input type="hidden" name="interval" value={selectedInterval} />
                  <button
                    type="submit"
                    className="btn-primary upgrade-btn"
                    disabled={isCheckoutLoading}
                  >
                    {isCheckoutLoading ? (
                      <>
                        <span className="loading-spinner" /> Processing...
                      </>
                    ) : (
                      "Upgrade Now"
                    )}
                  </button>
                </checkoutFetcher.Form>
              </div>
            </div>
          )}

          {billing.tier === "pro" && (
            <>
              <div className="billing-section">
                <h2>Payment Method</h2>
                <p className="section-description">
                  Manage your payment method and billing details in the Stripe Customer Portal.
                </p>
                <portalFetcher.Form
                  method="post"
                  action={`/organizations/${organization.id}/billing/portal`}
                >
                  <button type="submit" className="btn-secondary" disabled={isPortalLoading}>
                    {isPortalLoading ? (
                      <>
                        <span className="loading-spinner" /> Loading...
                      </>
                    ) : (
                      "Manage Payment Method"
                    )}
                  </button>
                </portalFetcher.Form>
              </div>

              {invoices.length > 0 && (
                <div className="billing-section">
                  <h2>Invoice History</h2>
                  <div className="invoice-list">
                    {invoices.map((invoice) => (
                      <div key={invoice.id} className="invoice-item">
                        <div className="invoice-info">
                          <span className="invoice-number">{invoice.number || "Draft"}</span>
                          <span className="invoice-date">
                            {new Date(invoice.created * 1000).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="invoice-amount">
                          {formatCurrency(invoice.amount, invoice.currency)}
                        </div>
                        <div className="invoice-actions">
                          {invoice.hostedInvoiceUrl && (
                            <a
                              href={invoice.hostedInvoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="invoice-link"
                            >
                              View
                            </a>
                          )}
                          {invoice.pdfUrl && (
                            <a
                              href={invoice.pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="invoice-link"
                            >
                              PDF
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!billing.cancelAtPeriodEnd && (
                <div className="billing-section cancel-section">
                  <h2>Cancel Subscription</h2>
                  <div className="cancel-content">
                    <div>
                      <p>
                        Your subscription will remain active until the end of your current billing
                        period. After that, your organization will be downgraded to the Free tier.
                      </p>
                      <p className="help-text">
                        Your data will be preserved, but you won't be able to add new members or
                        teams beyond Free tier limits.
                      </p>
                    </div>
                    {!showCancelConfirm ? (
                      <button
                        type="button"
                        className="btn-danger-outline"
                        onClick={() => setShowCancelConfirm(true)}
                      >
                        Cancel Subscription
                      </button>
                    ) : (
                      <div className="cancel-confirm">
                        <p className="confirm-text">Are you sure you want to cancel?</p>
                        <div className="confirm-actions">
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => setShowCancelConfirm(false)}
                            disabled={isCancelLoading}
                          >
                            Keep Subscription
                          </button>
                          <cancelFetcher.Form
                            method="post"
                            action={`/organizations/${organization.id}/billing/cancel`}
                          >
                            <button type="submit" className="btn-danger" disabled={isCancelLoading}>
                              {isCancelLoading ? (
                                <>
                                  <span className="loading-spinner" /> Canceling...
                                </>
                              ) : (
                                "Yes, Cancel"
                              )}
                            </button>
                          </cancelFetcher.Form>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {billing.tier === "enterprise" && (
            <div className="billing-section">
              <div className="billing-notice enterprise-notice">
                <h3>Enterprise Plan</h3>
                <p>
                  Your organization is on an Enterprise plan with custom limits. Contact your
                  account manager for billing inquiries.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
